import { useEffect, useState } from "react";
import { Button } from "../../../../components/ui/button";
import { ArrowLeft } from "lucide-react";
import Logo from "../../../../components/Logo/Logo";
import { Admin_Gender_LIST } from "../../../../constants/adminData";
import { gender } from "../../../../types/admin";
import { useDispatch } from "react-redux";
import { updatePersonalInfo } from "../../../../redux/slices/userProfileSlice";
import { saveSensitiveData } from '../../../../utils/secureStorage';
import { useAppSelector } from "../../../../store";

interface GenderSelectionProps {
  onContinue: (selectedGender?: gender | null) => void;
  onBack: () => void;
}

export default function GenderSelection({ onContinue, onBack }: GenderSelectionProps) {
  const dispatch = useDispatch();
  
  const [genders, setGenders] = useState<gender[]>([]);
  const [selectedGender, setSelectedGender] = useState<gender | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isButtonDisabled = !selectedGender;
  const profile = useAppSelector((state) => state.userProfile);

useEffect(() => {
  let ignore = false;

  const fetchGenders = async () => {
    try {
      const response = await Admin_Gender_LIST();
      if (ignore) return;

      const safeData = Array.isArray(response?.data) ? response.data : [];
      setGenders(safeData);
    } catch (err: any) {
      if (!ignore) setError(err.message || "Error loading genders");
    } finally {
      if (!ignore) setLoading(false);
    }
  };

  fetchGenders();
  return () => {
    ignore = true;
  };
}, []);

useEffect(() => {
  if (!genders.length) return; 

  const savedGenderId = profile.personalInfo.gender;
  if (!savedGenderId) return;

  const found = genders.find(
    (g) => g.genderId === Number(savedGenderId)
  );

  if (found) setSelectedGender(found);
}, [genders, profile.personalInfo.gender]);

  const handleGenderSelect = (gender: gender) => {
    setSelectedGender(gender);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isButtonDisabled && selectedGender) {
      await saveSensitiveData("selectedGender", JSON.stringify(selectedGender));
      await dispatch(updatePersonalInfo({
        gender: String(selectedGender.genderId) 
        }));
      onContinue();
      
    }
  };

  return (
    <div className="h-screen md:h-auto bg-[#F5F5F5] flex flex-col items-center px-6 pt-4 pb-12 relative">
      <div className="w-full max-w-xl flex flex-col items-center flex-1">
        
        <button onClick={onBack} className="absolute left-6 top-4 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center z-10" aria-label="Go back">
          <ArrowLeft className="w-6 h-6 text-brand-primary" />
        </button>

        <div className="flex justify-center mb-0">
          <Logo />
        </div>

        <h1 className="text-[26px] md:text-3xl lg:text-4xl font-semibold tracking-wide text-center mb-8">Gender</h1>

        {loading && <p className="text-center text-gray-500 mb-6">Loading genders...</p>}
        {error && <p className="text-center text-red-500 mb-6">{error}</p>}

        <form onSubmit={handleSubmit} className="w-full max-w-xl flex flex-col flex-1">
          
         <div className="w-full space-y-6">
            {(genders ?? []).map((gender) => (
              <button
                key={gender.genderId}
                type="button"
                onClick={() => handleGenderSelect(gender)}
                className={`w-full h-12 md:h-14 text-base md:text-lg lg:text-xl rounded-lg transition-all text-center ${
                  selectedGender?.genderId === gender.genderId
                    ? "bg-gradient-to-r from-brand-primary to-brand-gray p-[2px]"
                    : "border-2 border-gray-300 bg-white text-[#212121]"
                }`}
              >
                <span
                  className={`${
                    selectedGender?.genderId === gender.genderId
                      ? "flex items-center justify-center w-full h-full bg-white rounded-[6px] text-[#212121]"
                      : ""
                  }`}
                >
                  {gender.genderName}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-auto pt-10 pb-4 w-full">
            <Button
              type="submit"
              disabled={isButtonDisabled}
              className="w-full bg-brand-primary hover:bg-brand-hover text-white h-12 md:h-14 lg:h-16 text-lg md:text-xl lg:text-2xl font-medium rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed text-center"
            >
              Continue
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
